import React, { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import api from "../api";

const CartPage = () => {
  useEffect(() => {
    document.title = "Cart | Workgear365";
  }, []);
  const navigate = useNavigate();
  const { cart, updateQuantity, removeItem } = useContext(CartContext);
  const [shippingSettings, setShippingSettings] = useState({
    free_shipping_min: { "price": 150 },
    shipping_charge: { "price": 10.95 }
  });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountDetails, setDiscountDetails] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [newAddress, setNewAddress] = useState({
    first_name: "", last_name: "",  email: "", address: "", address2: "",
    city: "", state: "", zip: "", mobile: "",
  });
  const [saveAddress, setSaveAddress] = useState(false);
  const [error, setError] = useState("");          // <-- show errors here
  const safeCart = cart ? (Array.isArray(cart) ? cart : Object.values(cart)) : [];
  const totalPrice = safeCart.reduce((acc, i) => acc + parseFloat(i.salePrice || 0) * i.quantity, 0);
  
  // Read user and guard route
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  }, []);
  useEffect(() => {
  const fetchShipping = async () => {
    try {
      const res = await api.get("/api/homepage-data");
      
      setShippingSettings(res.data);
    } catch (err) {
      console.error("Failed to load shipping settings", err);
    }
  };

  fetchShipping();
}, []);
  useEffect(() => {
  //  if (!user?.id) {
      // not logged in → go to login
    //  navigate("/login");
    //}
  }, [user, navigate]);

  // Build a “profile” address card from user
  const profileAddress = useMemo(() => {
    if (!user || !user.id) return null;
    const fullName = (user.name || "").trim();
    const [first, ...rest] = fullName.split(" ");
    const last = rest.join(" ");
    return {
      id: "profile",
      client_id: user.id,
      source: "profile",
      first_name: first || "",
      last_name: last || "",
      email: user.email || "",   
      address: user.address || "",
      address2: user.address2 || "",
      city: user.city || "",
      state: user.state || "",
      zip: user.zip_code || user.zip || "",
      mobile: user.phone || "",
      country: user.country || "",
    };
  }, [user]);
  const applyDiscountCode = async () => {
    setDiscountError("");
    setDiscountDetails(null);

    if (!discountCode.trim()) {
      setDiscountError("Please enter a code");
      return;
    }

    try {
      const res = await api.post("/api/apply-discount", {
        code: discountCode,
        client_id: user.id,
        total_amount: totalPrice,
      });

      if (!res.data.valid) {
        setDiscountError(res.data.message);
        setDiscountCode("");  
        return;
      }

      setDiscountDetails(res.data);

    } catch (err) {
      setDiscountError("Something went wrong");
      setDiscountCode("");  
    }
  };

  // Load addresses and prepend profile card
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const res = await api.get("/api/client_addresses");
        const fromApi = res?.data?.addresses || [];
        const list = profileAddress ? [profileAddress, ...fromApi] : fromApi;
        setSavedAddresses(list);
      } catch (err) {
        console.error("Failed to fetch addresses", err);
        setSavedAddresses(profileAddress ? [profileAddress] : []);
      }
    };
    fetchAddresses();
  }, [profileAddress]);

  // helpers
  const handlePlus  = (k, q) => updateQuantity(k, q + 1);
  const handleMinus = (k, q) => updateQuantity(k, q > 1 ? q - 1 : 0);
  const handleRemove = (k) => removeItem(k);

  const handleAddressSelect = (addr) => {
    setSelectedAddress(addr);
    setShowNewAddress(false);
    setSaveAddress(false);
    setError(""); // clear any previous error
  };

  const handleNewAddressToggle = () => {
    setShowNewAddress(prev => !prev);
    setSelectedAddress(null);
    setError("");
  };

  const handleInputChange = (e) => {
    setNewAddress({ ...newAddress, [e.target.name]: e.target.value });
  };

  const withClientId = (addr) => ({
    ...addr,
    client_id: addr?.client_id ?? user?.id ?? null,
  });

  // Validation
  const validateNewAddress = (addr) => {
    const required = ["first_name","last_name","email","address","city","state","zip","mobile"];
    for (const f of required) {
      if (!addr?.[f] || String(addr[f]).trim() === "") {
        return `Please enter ${f.replace("_"," ")}`;
      }
    }
    return "";
  };

  const validateBeforeCheckout = () => {
    // must be logged in
   // if (!user?.id) return "Please login to continue.";
    // must select or add new address
    if (!selectedAddress && !showNewAddress) {
      return "Please select a saved address or add a new address.";
    }
    // if adding new, check fields
    if (showNewAddress) {
      const err = validateNewAddress(newAddress);
      if (err) return err;
    }
    return "";
  };
 const finalAmount = useMemo(() => {
          if (!discountDetails) return totalPrice;

          if (discountDetails.type === "percent") {
            return totalPrice - (totalPrice * discountDetails.value) / 100;
          }

          if (discountDetails.type === "fixed") {
            return totalPrice - discountDetails.value;
          }

          return totalPrice;
        }, [totalPrice, discountDetails]);
        const shippingCharge = useMemo(() => { 
          const min = Number(shippingSettings.free_shipping_min.price);
          const charge = Number(shippingSettings.shipping_charge.price);

          return finalAmount >= min ? 0 : charge;
        }, [finalAmount, shippingSettings]);
        const handleCheckout = async (e) => {
          e.preventDefault();
          setError("");

          const errMsg = validateBeforeCheckout();
          if (errMsg) {
            setError(errMsg);
            return;
          }

    const chosenAddress = selectedAddress || newAddress;
    const addressToUse = withClientId(chosenAddress);
    const uid = user?.id ?? null;

    try {
      // save newly created address
      if (saveAddress && !selectedAddress) {
        await api.post("/api/store_client_addresses", addressToUse);
        const res = await api.get("/api/client_addresses");
        const fromApi = res?.data?.addresses || [];
        setSavedAddresses(profileAddress ? [profileAddress, ...fromApi] : fromApi);
      }
      
      const discount_value = discountDetails? discountDetails.value:'';
      const discount_type = discountDetails? discountDetails.type:''; 
      // create stripe session
      const res = await api.post("/api/create-stripe-session", {
        cart,
        address: addressToUse,
        uid: uid,
        code:discountCode  ? discountCode  : null,
        discount_value: discount_value,
        discount_type: discount_type,
        amount_after_discount:parseFloat(finalAmount).toFixed(2),
      });

      window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout error", err);
      setError("Something went wrong during checkout. Please try again.");
    }
  };

 

  return (
    <div className="container my-5">
      <h2 className="text-center mb-4">Shopping Cart</h2>

      {/* error message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {safeCart.length > 0 ? (
        <form onSubmit={handleCheckout}>
          <div className="row">
            {/* Items */}
            <div className="col-md-8">
              {safeCart.map((item) => {
                const cart_key = `${item.product_id}_${item.color}_${item.size}`;
                return (
                  <div className="card mb-3 p-3 shadow-sm" key={cart_key}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-3">
                        <img src={item.product_image} className="img-fluid rounded" alt={item.product_title}/>
                      </div>
                      <div className="col-md-6">
                        <h5>{item.product_title}</h5>
                        <p className="text-muted">
                          <strong>Color:</strong> {item.color || "N/A"} | <strong>Size:</strong> {item.size || "N/A"}
                        </p>
                        <p><strong>Price:</strong> ${parseFloat(item.salePrice || 0).toFixed(2)}</p>
                      </div>
                      <div className="col-md-3 text-end">
                        <div className="d-flex align-items-center justify-content-end">
                          <button type="button" className="btn btn-sm btn-outline-secondary me-2" onClick={() => handleMinus(cart_key, item.quantity)}>-</button>
                          <input type="text" className="form-control text-center w-25" value={item.quantity} readOnly />
                          <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => handlePlus(cart_key, item.quantity)}>+</button>
                        </div>
                        <button type="button" className="btn btn-danger btn-sm mt-2" onClick={() => handleRemove(cart_key)}>Remove</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Address + Summary */}
            <div className="col-md-4">
              <div className="card p-3 shadow-sm mb-4">
                <h5>Select Delivery Address</h5>

                {savedAddresses.length > 0 && savedAddresses.map((addr) => (
                  <div
                    className={`card mb-2 p-2 ${selectedAddress && selectedAddress.id === addr.id ? "border-primary" : "border"}`}
                    key={`addr-${addr.id}`}
                    title={addr.source === "profile" ? "From your profile" : "Saved address"}
                  >
                    <label>
                      <input
                        type="radio"
                        name="address"
                        onChange={() => handleAddressSelect(addr)}
                        checked={selectedAddress?.id === addr.id}
                      />{" "}
                      <strong>
                        {addr.first_name} {addr.last_name}
                      </strong>
                      <br />
                      {addr.address}{addr.address2 ? `, ${addr.address2}` : ""}
                      <br />
                      {addr.city}, {addr.state}, {addr.zip}
                      <br />
                      Mobile: {addr.mobile}
                    </label>
                  </div>
                ))}

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={showNewAddress}
                    onChange={handleNewAddressToggle}
                  />
                  <label className="form-check-label">Add a new address</label>
                </div>
              </div>

              {showNewAddress && (
                <div className="card p-3 shadow-sm mb-3">
                  <input
                    type="email"
                    name="email"
                    placeholder="EMAIL"
                    className="form-control mb-2"
                    value={newAddress.email}
                    onChange={handleInputChange}
                    required
                  />
                  {["first_name","last_name","address","address2","city","state","zip","mobile"].map((field) => (
                    <input
                      key={field}
                      type="text"
                      name={field}
                      placeholder={field.replace("_", " ").toUpperCase()}
                      className="form-control mb-2"
                      value={newAddress[field]}
                      onChange={handleInputChange}
                      required={["first_name","last_name","address","city","state","zip","mobile"].includes(field)}
                    />
                  ))}

                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={saveAddress}
                      onChange={() => setSaveAddress(prev => !prev)}
                    />
                    <label className="form-check-label">Save this address to my account</label>
                  </div>
                </div>
              )}
              <div className="mb-3">
              <label className="form-label"><strong>Do you have a discount code?</strong></label>
              <div className="d-flex">
                <input
                  type="text"
                  className="form-control me-2"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value);
                    setDiscountError("");
                    setDiscountDetails(null);   // ✅ Reset discount when user edits code
                  }}
                />

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={applyDiscountCode}
                >
                  Apply
                </button>
              </div>

              {discountError && <p className="text-danger mt-1">{discountError}</p>}

              {discountDetails && (
                <p className="text-success mt-1">
                  {discountDetails.message}
                </p>
              )}
            </div>

              <div className="card p-3 shadow-sm">
                <h4 className="mb-3">Cart Summary</h4>
                <p><strong>Subtotal:</strong> ${totalPrice.toFixed(2)}</p>

                {discountDetails && (
                  <p className="text-success">
                    Discount: 
                    {discountDetails.type === 'percent'
                      ? discountDetails.value + "%"
                      : "$" + discountDetails.value}
                  </p>
                )}
                <p><strong>Shipping:</strong> ${shippingCharge.toFixed(2)}</p>
                <h4>
                  <strong>Total:</strong> ${(finalAmount + shippingCharge).toFixed(2)}
                </h4>

                <button type="submit" className="btn btn-success w-100">
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="text-center"><h4>Your cart is empty!</h4></div>
      )}
    </div>
  );
};

export default CartPage;
